import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, isSameDay, parseISO, differenceInDays } from "date-fns";
import { Plus, LogOut, Calendar as CalendarIcon, Bell, Check, Trash2, Edit, ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface LaunchPhase {
  id: string;
  title: string;
  description: string | null;
  phase_date: string;
  reminder_date: string | null;
  phase_type: string;
  completed: boolean;
  color: string;
}

export default function LaunchCalendar() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [phases, setPhases] = useState<LaunchPhase[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<LaunchPhase | null>(null);
  
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [phaseDate, setPhaseDate] = useState<Date>(new Date());
  const [reminderDate, setReminderDate] = useState<Date | undefined>();
  const [phaseType, setPhaseType] = useState("milestone");
  const [color, setColor] = useState("#8B5CF6");

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) {
        navigate("/auth");
      } else {
        setUser(user);
        fetchPhases(user.id);
      }
    });

    checkReminders();
    const interval = setInterval(checkReminders, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchPhases = async (userId: string) => {
    const { data, error } = await supabase
      .from("launch_phases")
      .select("*")
      .eq("user_id", userId)
      .order("phase_date", { ascending: true });

    if (error) {
      toast.error("Failed to load launch phases");
      console.error(error);
    } else {
      setPhases(data || []);
    }
  };

  const checkReminders = async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from("launch_phases")
      .select("*")
      .eq("user_id", user.id)
      .eq("completed", false);

    if (data) {
      const today = new Date();
      data.forEach((phase) => {
        const phaseDate = parseISO(phase.phase_date);
        const daysUntil = differenceInDays(phaseDate, today);
        
        if (phase.reminder_date) {
          const reminderDate = parseISO(phase.reminder_date);
          if (isSameDay(today, reminderDate)) {
            toast.info(`Reminder: ${phase.title} is coming up!`, {
              description: `Phase date: ${format(phaseDate, "PPP")}`,
              duration: 10000,
            });
          }
        }
        
        if (daysUntil <= 3 && daysUntil >= 0) {
          const lastShown = localStorage.getItem(`reminder-${phase.id}`);
          const lastShownDate = lastShown ? new Date(lastShown) : null;
          
          if (!lastShownDate || !isSameDay(today, lastShownDate)) {
            toast.warning(`${phase.title} is ${daysUntil === 0 ? 'today' : `in ${daysUntil} day${daysUntil > 1 ? 's' : ''}`}!`, {
              icon: <Bell className="h-4 w-4" />,
              duration: 10000,
            });
            localStorage.setItem(`reminder-${phase.id}`, today.toISOString());
          }
        }
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (error) {
      console.log("Logout global failed, clearing local session:", error);
      await supabase.auth.signOut({ scope: 'local' });
    } finally {
      // Always navigate away
      navigate("/auth");
    }
  };

  const openDialog = (phase?: LaunchPhase) => {
    if (phase) {
      setEditingPhase(phase);
      setTitle(phase.title);
      setDescription(phase.description || "");
      setPhaseDate(parseISO(phase.phase_date));
      setReminderDate(phase.reminder_date ? parseISO(phase.reminder_date) : undefined);
      setPhaseType(phase.phase_type);
      setColor(phase.color);
    } else {
      setEditingPhase(null);
      setTitle("");
      setDescription("");
      setPhaseDate(selectedDate);
      setReminderDate(undefined);
      setPhaseType("milestone");
      setColor("#8B5CF6");
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user || !title) {
      toast.error("Please fill in all required fields");
      return;
    }

    const phaseData = {
      user_id: user.id,
      title,
      description: description || null,
      phase_date: format(phaseDate, "yyyy-MM-dd"),
      reminder_date: reminderDate ? format(reminderDate, "yyyy-MM-dd") : null,
      phase_type: phaseType,
      color,
    };

    if (editingPhase) {
      const { error } = await supabase
        .from("launch_phases")
        .update(phaseData)
        .eq("id", editingPhase.id);

      if (error) {
        toast.error("Failed to update phase");
        console.error(error);
      } else {
        toast.success("Phase updated successfully");
        fetchPhases(user.id);
      }
    } else {
      const { error } = await supabase
        .from("launch_phases")
        .insert([phaseData]);

      if (error) {
        toast.error("Failed to create phase");
        console.error(error);
      } else {
        toast.success("Phase created successfully");
        fetchPhases(user.id);
      }
    }

    setIsDialogOpen(false);
  };

  const toggleComplete = async (phase: LaunchPhase) => {
    const { error } = await supabase
      .from("launch_phases")
      .update({ completed: !phase.completed })
      .eq("id", phase.id);

    if (error) {
      toast.error("Failed to update phase");
    } else {
      toast.success(phase.completed ? "Phase marked incomplete" : "Phase completed!");
      if (user) fetchPhases(user.id);
    }
  };

  const deletePhase = async (id: string) => {
    const { error } = await supabase
      .from("launch_phases")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete phase");
    } else {
      toast.success("Phase deleted");
      if (user) fetchPhases(user.id);
    }
  };

  const getPhasesForDate = (date: Date) => {
    return phases.filter(phase => isSameDay(parseISO(phase.phase_date), date));
  };

  const upcomingPhases = phases
    .filter(p => !p.completed && new Date(p.phase_date) >= new Date())
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2 text-2xl font-bold text-primary">
            <ArrowLeft className="w-5 h-5" />
            Launch Calendar
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Launch Phase Calendar</h1>
              <p className="text-muted-foreground">
                Plan and track your launch milestones with automated reminders
              </p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openDialog()} size="lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Phase
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingPhase ? "Edit Phase" : "Create New Phase"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Product Launch, Beta Testing"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add details about this phase..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Phase Date *</Label>
                      <Calendar
                        mode="single"
                        selected={phaseDate}
                        onSelect={(date) => date && setPhaseDate(date)}
                        className="rounded-md border pointer-events-auto"
                      />
                    </div>
                    <div>
                      <Label>Reminder Date (Optional)</Label>
                      <Calendar
                        mode="single"
                        selected={reminderDate}
                        onSelect={setReminderDate}
                        className="rounded-md border pointer-events-auto"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type">Phase Type</Label>
                      <Select value={phaseType} onValueChange={setPhaseType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="milestone">Milestone</SelectItem>
                          <SelectItem value="deadline">Deadline</SelectItem>
                          <SelectItem value="meeting">Meeting</SelectItem>
                          <SelectItem value="launch">Launch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="color">Color</Label>
                      <Input
                        id="color"
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>
                    {editingPhase ? "Update" : "Create"} Phase
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Calendar View</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border w-full pointer-events-auto"
                  modifiers={{
                    hasPhase: phases.map(p => parseISO(p.phase_date)),
                  }}
                  modifiersStyles={{
                    hasPhase: {
                      fontWeight: 'bold',
                      backgroundColor: 'hsl(var(--primary) / 0.1)',
                    },
                  }}
                />
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Upcoming Phases
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {upcomingPhases.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No upcoming phases. Add one to get started!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {upcomingPhases.map((phase) => (
                        <div
                          key={phase.id}
                          className="p-3 rounded-lg border"
                          style={{ borderLeft: `4px solid ${phase.color}` }}
                        >
                          <div className="font-medium">{phase.title}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {format(parseISO(phase.phase_date), "PPP")}
                          </div>
                          <Badge variant="secondary" className="mt-2">
                            {phase.phase_type}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                    {format(selectedDate, "MMMM d, yyyy")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {getPhasesForDate(selectedDate).length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No phases scheduled for this date
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {getPhasesForDate(selectedDate).map((phase) => (
                        <div
                          key={phase.id}
                          className="p-3 rounded-lg border space-y-2"
                          style={{ borderLeft: `4px solid ${phase.color}` }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className={`font-medium ${phase.completed ? 'line-through text-muted-foreground' : ''}`}>
                                {phase.title}
                              </div>
                              {phase.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {phase.description}
                                </p>
                              )}
                              <Badge variant="secondary" className="mt-2">
                                {phase.phase_type}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={phase.completed ? "outline" : "default"}
                              onClick={() => toggleComplete(phase)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDialog(phase)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deletePhase(phase.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Launch Phases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {phases.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No launch phases yet. Create your first one to start planning!
                  </p>
                ) : (
                  phases.map((phase) => (
                    <div
                      key={phase.id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors"
                      style={{ borderLeft: `4px solid ${phase.color}` }}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <Button
                          size="sm"
                          variant={phase.completed ? "default" : "outline"}
                          onClick={() => toggleComplete(phase)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <div className="flex-1">
                          <div className={`font-medium ${phase.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {phase.title}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(parseISO(phase.phase_date), "PPP")}
                          </div>
                        </div>
                        <Badge variant="secondary">{phase.phase_type}</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDialog(phase)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deletePhase(phase.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
